import { isFilled } from "@/ipqc/lib/utils"
import { toPathString } from "@/lib/form/form"
import {
  ParamSchemaMode,
  ParamSchemaType,
  ParamValidationType,
} from "@/lib/form/schema"
import { FormParamStatus } from "@/lib/form/types"
import { createUuid, EnumsOf } from "@/lib/utils"
import {
  buildSchemaForGeneralParameter,
  buildSchemaForPmProduct,
  buildSchemaForRmProduct,
  Schema,
  schemaPathToValuePath,
} from "@/rmpm/lib/schema"
import { api } from "@/services/base"
import useStore from "@/store"
import {
  Handover,
  HandoverTrucks,
  PMTruck,
  RMTruck,
  TruckParameter,
} from "@/store/rmpm"
import { LabelValue } from "@/types/common"
import { get } from "lodash"

export interface StartRMPMProcessPayload {
  processId: string
  shiftSessionId: string
}

export async function startRMPMProcess(payload: StartRMPMProcessPayload) {
  return api
    .post("rmpm/process", {
      json: payload,
    })
    .json()
}

export async function discardRMPMProcess(): Promise<unknown> {
  await api.post("rmpm/process/discard")
  const store = useStore.getState()
  store.rmpm.clearProcess()

  return
}

export async function endRMPMProcess(): Promise<unknown> {
  await api.post("rmpm/process/end")
  const store = useStore.getState()
  store.session.endProcess(store.rmpm.process.processId)
  store.rmpm.clearProcess()

  return
}

export async function getRMPMResample(): Promise<unknown> {
  return api.get("rmpm/sheets/resample").json()
}

// ---------- Create Sheet API (for server-generated IDs) ----------

export type RMPMSheetType = "RM" | "PM_CARTON" | "PM_LAMINATE"

export interface CreateRMPMSheetRequest {
  truckNumber: string
  truckType: RMPMSheetType
  schema?: string
}

export interface CreateRMPMSheetResponse {
  sheetId: string
  truckNumber: string
  truckType: RMPMSheetType
  status: string
  sessionId: string
  createdAt: string
  isNew: boolean
}

/**
 * Create a new RMPM sheet (truck) on the server.
 * This should be called when the user adds a truck, before filling out readings.
 * Returns a server-generated sheetId that must be used for all subsequent operations.
 */
export async function createRMPMSheet(
  payload: CreateRMPMSheetRequest
): Promise<CreateRMPMSheetResponse> {
  const res = await api.post<CreateRMPMSheetResponse>("rmpm/sheets/create", {
    json: payload,
  })
  return res.json()
}

// ---------- Create Product Sheet API (for server-generated IDs) ----------

export interface CreateRMPMProductSheetRequest {
  productId: string
  productName: string
  sku?: string
  market?: "DOMESTIC" | "EXPORT"
}

export interface CreateRMPMProductSheetResponse {
  productSheetId: string
  sheetId: string
  productId: string
  productName: string
  status: string
  sku?: string
  market?: "DOMESTIC" | "EXPORT"
  sequenceOrder: number
  createdAt: string
  isNew: boolean
}

/**
 * Create a new product sheet for a truck on the server.
 * This should be called when the user adds a product to a truck, before filling out readings.
 * Returns a server-generated productSheetId that must be used for all subsequent operations.
 */
export async function createRMPMProductSheet(
  sheetId: string,
  payload: CreateRMPMProductSheetRequest
): Promise<CreateRMPMProductSheetResponse> {
  const res = await api.post<CreateRMPMProductSheetResponse>(
    `rmpm/sheets/${sheetId}/products`,
    {
      json: payload,
    }
  )
  return res.json()
}

// ---------- Session Sheets (for cross-device sync) ----------

export interface SessionSheetParameter {
  parameterId: string
  name: string
  value: string
  remark: string | null
  status: string
  recordedAt: string
  inputType: string
}

export type SessionSheetTruckParameter = SessionSheetParameter
export type SessionSheetProductParameter = SessionSheetParameter

export interface SessionSheetProduct {
  id: string
  productId: string | null
  name: string
  status: string
  sku?: Array<{
    name: string
    market: {
      domestic?: { parameters: SessionSheetProductParameter[] }
      export?: { parameters: SessionSheetProductParameter[] }
    }
  }>
  market?: {
    domestic?: { parameters: SessionSheetProductParameter[] }
    export?: { parameters: SessionSheetProductParameter[] }
  }
  parameters?: SessionSheetProductParameter[]
  resample?: boolean
}

export interface SessionSheet {
  id: string
  truckNumber: string
  type: string
  status: string
  sheetType?: "PROCESS" | "HANDOVER" | "RESAMPLE" | null // Optional - older data may not have this field
  handoverId: string | null
  truckParameters: SessionSheetTruckParameter[]
  products: SessionSheetProduct[]
  // Note: schema is not needed - we build it from product definitions and sheet data
}

export interface GetSessionSheetsResponse {
  sheets: SessionSheet[]
}

/**
 * Get all sheets for a specific employee session (for cross-device sync)
 * This allows fetching current session data when logging in from a different device.
 */
export async function getSessionSheets(
  employeeSessionId: string
): Promise<GetSessionSheetsResponse> {
  const res = await api.get<GetSessionSheetsResponse>(
    `rmpm/session-sheets?employeeSessionId=${employeeSessionId}`
  )
  return res.json()
}

export interface RMProductSchema {
  id: string
  name: string
  parameters: ParamAPISchema[]
}

export interface RMPMParameterSchema {
  id: string
  inputType: ParamSchemaType
  mandatory: boolean
  mode: ParamSchemaMode
  name: string
  validationType: ParamValidationType
  isEditable: boolean
  possibleValues?: string[]
  correctValue?: string
}

export interface ParamAPISchema {
  id?: string
  parameterId?: string
  productId?: string
  name: string
  inputType: ParamSchemaType
  validationType: ParamValidationType
  mandatory: boolean
  mode: ParamSchemaMode
  unit?: string
  possibleValues?: string[]
  correctValue?: string
  minLimit?: number
  maxLimit?: number
}

export interface PMSku {
  id: string
  name: string
  market: {
    domestic: {
      parameters: ParamAPISchema[]
    }
    export: {
      parameters: ParamAPISchema[]
    }
  }
}

export interface PMProductSchema {
  id: string
  packagingType: string
  name: string
  market?: {
    domestic: {
      parameters: ParamAPISchema[]
    }
    export: {
      parameters: ParamAPISchema[]
    }
  }
  sku?: PMSku[]
}

export interface GetProductsRes {
  rmProducts: RMProductSchema[]
  pmProducts: PMProductSchema[]
  pmParameters: RMPMParameterSchema[]
  rmParameters: RMPMParameterSchema[]
}

export async function getProducts(): Promise<GetProductsRes> {
  const res = await api.get<GetProductsRes>("rmpm/products").json()
  const store = useStore.getState()

  store.rmpm.setAllProducts(res)

  return res
}

export interface RMPMSubmitPayload {
  sheetId: string // Server-generated sheet ID (REQUIRED)
  status: string
  parameters: RMPMTRuckParameter[]
  products: RMPMProduct[]
  schema?: string
}

export interface RMPMTRuckParameter {
  parameterId: string // Server parameter ID (REQUIRED)
  value?: string | number | boolean
  remark?: string
  recordedAt?: string
}

export interface RMPMProduct {
  productSheetId: string // Server-generated product sheet ID (REQUIRED)
  status: FormParamStatus
  sku?: string
  market?: "DOMESTIC" | "EXPORT"
  parameters: RMPMProductParameter[]
}

export interface RMPMProductParameter {
  parameterId: string // Server parameter ID (REQUIRED)
  value?: string | number | boolean
  remark?: string
  recordedAt?: string
}

export const RmPmFormTruckType = {
  NONE: "NONE",
  PM_CARTON: "PM_CARTON",
  PM_LAMINATE: "PM_LAMINATE",
  RM: "RM",
} as const
export type RmPmFormTruckType = EnumsOf<typeof RmPmFormTruckType>

export async function submitRMPMForm({
  schema,
  formData,
  truckType,
  status,
}: {
  schema: Schema
  truckType: "rm" | "pm"
  formData: Record<string, any>
  status: FormParamStatus
}) {
  let metaSchema: Schema = useStore
    .getState()
    .rmpm.getTruckMetaSchemaById(truckType, schema.sheetId || "") as Schema

  let metaForm = useStore
    .getState()
    .rmpm.getTruckFormById(truckType, schema.sheetId || "")

  if (!metaSchema || Object.keys(metaSchema).length === 0) {
    throw new Error("Meta schema not found for truck")
  }

  const truckNumber = schema.properties?.truckNumber.const as string | undefined
  if (!truckNumber) {
    throw new Error("Truck number is missing")
  }

  // Get sheetId from the schema (server-generated) - REQUIRED
  const sheetId = schema.sheetId as string | undefined
  if (!sheetId) {
    throw new Error("Sheet ID is required. Please add the truck again.")
  }

  const payload: RMPMSubmitPayload = {
    sheetId: sheetId,
    status: status,
    parameters: [],
    products: [],
    schema: JSON.stringify({ ...metaSchema, formData: metaForm ?? {} }),
  }

  if (!schema.properties?.general?.items?.length) {
    throw new Error("General parameters are missing")
  }
  const generalParamPrefix = "general.items"
  for (const [index, param] of schema.properties.general.items.entries()) {
    const path = toPathString([generalParamPrefix, `${index}`])
    const paramValue = get(formData, schemaPathToValuePath(path))
    if (!paramValue) {
      throw new Error("General parameter missing in value")
    }

    payload.parameters.push({
      parameterId: param.id || "",
      value: paramValue.value || "",
      remark: paramValue.remark || "",
      recordedAt: paramValue.recordedAt || "",
    })
  }

  if (!schema.properties?.products?.items?.length) {
    throw new Error("Product parameters are missing")
  }

  const productPrefix = (index: number) => `products.items[${index}]`
  
  // Track productSheetIds to prevent duplicates
  const addedProductSheetIds = new Set<string>()
  
  for (const [
    productIdx,
    product,
  ] of schema.properties.products.items.entries()) {
    const productPath = productPrefix(productIdx)
    const productValue = buildApiProduct(product, productPath, formData, status)
    if (!productValue) {
      continue
    }

    // Skip if this productSheetId was already added
    if (addedProductSheetIds.has(productValue.productSheetId)) {
      console.warn(
        `[submitRMPMForm] Skipping duplicate product: ${productValue.productSheetId}`
      )
      continue
    }
    addedProductSheetIds.add(productValue.productSheetId)
    payload.products.push(productValue)

    if (product.properties?.skus?.items?.length) {
      for (const [skuIdx, sku] of product.properties.skus.items.entries()) {
        const skuPath = `${productPath}.properties.skus.items[${skuIdx}]`
        const skuValue = buildApiProduct(sku, skuPath, formData, status)
        if (!skuValue) {
          continue
        }

        // Skip if this productSheetId was already added
        if (addedProductSheetIds.has(skuValue.productSheetId)) {
          console.warn(
            `[submitRMPMForm] Skipping duplicate SKU: ${skuValue.productSheetId}`
          )
          continue
        }
        addedProductSheetIds.add(skuValue.productSheetId)
        payload.products.push(skuValue)
      }
    }
  }

  return api.post("rmpm/sheets", {
    json: [payload],
  })
}

function buildApiProduct(
  schema: Schema,
  pathPrefix: string,
  formData: Record<string, any>,
  status: FormParamStatus
): RMPMProduct | null {
  const productPath = pathPrefix
  if (!schema.properties?.parameters.items) {
    return null
  }

  // productSheetId is REQUIRED
  const productSheetId = schema.productSheetId
  if (!productSheetId) {
    throw new Error(
      `Product sheet ID is required for ${schema.options?.label}. Please add the product again.`
    )
  }

  const product = get(formData, schemaPathToValuePath(pathPrefix))

  let productStatus: FormParamStatus
  const isRejectRequested = Boolean(product?.__rejectRequested)
  const isRejectedProduct =
    product?.status === FormParamStatus.REJECTED_APPROVED ||
    (status === FormParamStatus.SUBMITTED &&
      (product?.status === FormParamStatus.REJECTED_PENDING_APPROVAL ||
        isRejectRequested))

  if (isRejectedProduct) {
    productStatus = FormParamStatus.REJECTED_APPROVED
  } else if (status === FormParamStatus.SUBMITTED && product?.resample !== true) {
    productStatus = FormParamStatus.SUBMITTED
  } else {
    productStatus = status
  }

  const productValue: RMPMProduct = {
    productSheetId: productSheetId,
    status: productStatus,
    parameters: [],
  }

  const productParamPrefix = (paramIdx: number) =>
    toPathString([
      productPath,
      "properties",
      "parameters",
      "items",
      `${paramIdx}`,
    ])

  // First pass: collect all parameters and check for any remarks
  const parameters: Array<{
    parameterId: string
    value: string
    remark: string
    recordedAt: string
    hasRemark: boolean
  }> = []
  let selectedMarket: string | undefined
  let selectedSkuValue: string | undefined
  let skuParamSchema: Schema | undefined
  
  for (const [
    paramIdx,
    param,
  ] of schema.properties.parameters.items.entries()) {
    const path = productParamPrefix(paramIdx)
    const paramValue = get(formData, schemaPathToValuePath(path))

    if (param.options?.label === "Market") {
      selectedMarket =
        typeof paramValue?.value === "string" ? paramValue.value : undefined
      continue
    }

    if (param.options?.label === "SKU") {
      selectedSkuValue =
        typeof paramValue?.value === "string" ? paramValue.value : undefined
      skuParamSchema = param
      continue
    }

    if (!isFilled(paramValue)) {
      throw new Error(`Value missing for ${param.options?.label}`)
    }

    parameters.push({
      parameterId: param.id || "",
      value: paramValue.value || "",
      remark: paramValue.remark || "",
      recordedAt: paramValue.recordedAt || "",
      hasRemark: isFilled(paramValue.remark),
    })
  }

  // Determine final status based on whether ANY parameter has a remark
  if (status === FormParamStatus.SUBMITTED && product?.resample !== true) {
    if (productValue.status !== FormParamStatus.REJECTED_APPROVED) {
      const hasAnyRemarks = parameters.some(p => p.hasRemark)
      if (hasAnyRemarks) {
        productValue.status = FormParamStatus.SUBMITTED_PENDING_APPROVAL
      } else {
        productValue.status = FormParamStatus.SUBMITTED
      }
    }
  }

  // Assign parameters without the hasRemark flag
  productValue.parameters = parameters.map(
    ({ hasRemark: __hasRemark, ...rest }) => rest
  )

  const normalizedMarket = normalizeMarket(selectedMarket)
  if (normalizedMarket) {
    productValue.market = normalizedMarket
  }

  const resolvedSku = resolveSkuLabel(skuParamSchema, selectedSkuValue)
  if (resolvedSku) {
    productValue.sku = resolvedSku
  }

  return productValue
}

function normalizeMarket(
  value: string | undefined
): "DOMESTIC" | "EXPORT" | undefined {
  if (!value) return undefined

  const normalized = value.trim().toUpperCase()
  if (normalized === "DOMESTIC") return "DOMESTIC"
  if (normalized === "EXPORT") return "EXPORT"

  return undefined
}

function resolveSkuLabel(
  paramSchema: Schema | undefined,
  rawValue: string | undefined
): string | undefined {
  if (!rawValue) return undefined

  const choices = paramSchema?.options?.choices
  if (!Array.isArray(choices)) {
    return rawValue
  }

  const matchedChoice = choices.find((choice: any) => {
    return choice?.id === rawValue || choice?.value === rawValue
  })

  if (typeof matchedChoice?.label === "string" && matchedChoice.label.trim()) {
    return matchedChoice.label
  }

  return rawValue
}

export async function loadSchemas(rmTrucks: RMTruck[], pmTrucks: PMTruck[]) {
  const rmpmStore = useStore.getState().rmpm
  const pmSchemas: Schema[] = []
  const rmSchemas: Schema[] = []

  for (const truck of pmTrucks) {
    /* -------------------------------
       1. BASE SCHEMA FOR TRUCK
    -------------------------------- */

    const newSchema: Schema = {
      isHandover: false,
      sheetId: truck.sheetId, // Server-generated sheet ID
      type: "object",

      options: {
        label: truck.truckNumber,
      },

      properties: {
        id: {
          type: "string",
          default: truck.id,
          const: truck.id,
        },

        truckNumber: {
          type: "string",
          const: truck.truckNumber,
        },

        general: {
          type: "array",
          options: {
            widget: ParamSchemaType.CARD,
          },
          items: [],
        },

        products: {
          type: "array",
          options: {
            widget: ParamSchemaType.SECTION,
            addable: true,
            label: "Products",
          },
          items: [],
        },
      },
    }

    /* -------------------------------
       2. GENERAL PARAMETERS
    -------------------------------- */

    const generalSchema = newSchema.properties?.general as Schema

    if (!generalSchema.items) {
      generalSchema.items = []
    }

    const pmProductApiSchema = rmpmStore.getPmParametersApiSchema()

    if (pmProductApiSchema?.length) {
      const generalParameters = buildSchemaForGeneralParameter(
        pmProductApiSchema ?? [],
        truck
      )

      if (
        generalParameters.some((param) => param.options?.label === "PM Type")
      ) {
        generalSchema.items.push(...generalParameters)
      } else {
        const pmTypeSet = new Set<string>()
        const pmTypeList: LabelValue[] = []

        for (const product of rmpmStore.allProducts.pmProducts) {
          if (!pmTypeSet.has(product.packagingType)) {
            pmTypeList.push({
              id: product.packagingType,
              label: product.packagingType,
              value: product.packagingType,
            })
          }

          pmTypeSet.add(product.packagingType)
        }

        if (pmTypeList.length > 0) {
          const paramID = createUuid()

          const baseItem: Schema = {
            id: paramID,
            type: "object",
            required: true,
            options: {
              label: "PM Type",
              widget: ParamSchemaType.VALUE_TEXT,
              mode: ParamSchemaMode.INFO,
              disabled: true,
            },
            properties: {
              id: {
                type: "string",
                default: paramID,
                required: true,
              },
              recordedAt: {
                type: "string",
                required: true,
              },
              value: {
                type: "string",
                required: true,
              },
            },
          }

          baseItem.properties!.value!.default = truck.pmType

          generalSchema.items.push(baseItem, ...generalParameters)
        } else {
          generalSchema.items.push(...generalParameters)
        }
      }
    }

    /* -------------------------------
       3. PRODUCT PARAMETERS
    -------------------------------- */

    const productSchema = newSchema.properties?.products as Schema

    if (!productSchema.items) {
      productSchema.items = []
    }

    // Get productSheetIds from truck (if available from onboarding)
    const pmProductSheetIds = (truck as any).productSheetIds as
      | Record<string, string>
      | undefined

    for (const productId of truck.products) {
      const pmApiSchema = rmpmStore.getPmProductApiSchema(productId)

      if (!pmApiSchema) {
        continue
      }

      const schemaPath = `properties.products.items[${productSchema.items.length}]`

      const productSchemaItem = buildSchemaForPmProduct(pmApiSchema, schemaPath)

      // Apply productSheetId from the truck's productSheetIds map
      if (pmProductSheetIds && pmProductSheetIds[productId]) {
        productSchemaItem.productSheetId = pmProductSheetIds[productId]
      }

      productSchema.items.push(productSchemaItem)
    }

    /* -------------------------------
       4. ADD TO FINAL SCHEMA LIST
    -------------------------------- */

    pmSchemas.push(newSchema)
  }

  for (const truck of rmTrucks) {
    /* ------------------------------
       1. Base Schema
    ------------------------------ */

    const newSchema: Schema = {
      isHandover: false,
      sheetId: truck.sheetId, // Server-generated sheet ID

      options: {
        label: truck.truckNumber,
      },

      type: "object",

      properties: {
        id: {
          type: "string",
          default: truck.id,
          const: truck.id,
        },

        truckNumber: {
          type: "string",
          const: truck.truckNumber,
        },

        general: {
          type: "array",
          options: {
            widget: ParamSchemaType.CARD,
            label: "General Parameters",
            hideLabel: true,
          },
          items: [],
        },

        products: {
          type: "array",
          options: {
            label: "Products",
            addable: true,
            widget: ParamSchemaType.SECTION,
          },
          items: [],
        },
      },
    }

    /* ------------------------------
       2. GENERAL PARAMETERS
    ------------------------------ */

    const generalSchema = newSchema.properties?.general as Schema

    if (!generalSchema.items) {
      generalSchema.items = []
    }

    const rmProductApiSchema = rmpmStore.getRmParametersApiSchema()

    if (rmProductApiSchema?.length) {
      const generalItems = buildSchemaForGeneralParameter(rmProductApiSchema)
      generalSchema.items.push(...generalItems)
    }

    /* ------------------------------
       3. PRODUCT PARAMETERS
    ------------------------------ */

    const productSchema = newSchema.properties?.products as Schema

    if (!productSchema.items) {
      productSchema.items = []
    }

    // Get productSheetIds from truck (if available from onboarding)
    const rmProductSheetIds = (truck as any).productSheetIds as
      | Record<string, string>
      | undefined

    for (const productId of truck.products) {
      const rmApiSchema = rmpmStore.getRmProductApiSchema(productId)

      if (!rmApiSchema) {
        continue
      }

      const productBlock = buildSchemaForRmProduct(rmApiSchema)

      // Apply productSheetId from the truck's productSheetIds map
      if (rmProductSheetIds && rmProductSheetIds[productId]) {
        productBlock.productSheetId = rmProductSheetIds[productId]
      }

      productSchema.items.push(productBlock)
    }

    /* ------------------------------
       4. PUSH FINAL TRUCK SCHEMA
    ------------------------------ */

    rmSchemas.push(newSchema)
  }

  await rmpmStore.addRmTrucksMetaSchemas(rmSchemas)
  await rmpmStore.addPmTrucksMetaSchemas(pmSchemas)
}

export interface GetDraftSheetsRes {
  processSessionId: string
  processId: string
  sheets: TruckSheet[]
}

export interface TruckSheet {
  sheetId: string
  truckNumber: string
  type: string
  status: FormParamStatus
  schema: Schema | null
  truckParameters: TruckParameter
  products: TruckProduct[]
}

export interface TruckProduct {
  id: string
  name: string
  status: string
  parameters: ProductParamter[]
}

export interface ProductParamter {
  parameterId: string
  name: string
  value: string
  remark: string | null
  status: string
  recordedAt: string
  inputType: string
}

export async function getDraftSheets() {
  const res = await api.get<GetDraftSheetsRes>("rmpm/sheets/draft").json()

  const formatted: GetDraftSheetsRes = {
    ...res,
    sheets: res.sheets.map((sheet) => {
      let schema: Schema = {}
      try {
        schema = JSON.parse(sheet.schema as string)
      } catch {
        schema = {}
      }

      return {
        ...sheet,
        schema,
      }
    }),
  }

  return formatted
}

/**
 * Delete a product sheet from a truck (using new refactored API)
 * DELETE /rmpm/sheets/:sheetId/products/:productSheetId
 */
export async function deleteRMPMProductSheet(
  sheetId: string,
  productSheetId: string
): Promise<void> {
  await api.delete(`rmpm/sheets/${sheetId}/products/${productSheetId}`)
}

function pickProductByProductId(
  truckProducts: any[],
  productId: string
): any | null {
  if (!productId) return null

  const candidates = truckProducts.filter((p: any) => p.productId === productId)
  if (!candidates.length) return null

  const submittedCandidate = candidates.find((p: any) =>
    isSubmittedLikeStatus(p?.status) ||
    p?.status === FormParamStatus.REJECTED_PENDING_APPROVAL ||
    p?.status === FormParamStatus.REJECTED_APPROVED
  )
  if (submittedCandidate) return submittedCandidate

  const skuCandidate = candidates.find((p: any) =>
    (p?.sku ?? []).some(
      (sku: any) =>
        (sku?.market?.domestic?.parameters?.length ?? 0) > 0 ||
        (sku?.market?.export?.parameters?.length ?? 0) > 0
    )
  )
  if (skuCandidate) return skuCandidate

  const parameterCandidate = candidates.find(
    (p: any) => (p?.parameters?.length ?? 0) > 0
  )
  if (parameterCandidate) return parameterCandidate

  return candidates[0]
}

function resolveProductFromSchema(
  schema: any,
  truckProducts: any[]
): { product: any; productId: string } {
  const productSheetId = schema?.productSheetId

  let productId =
    schema?.properties?.parameters?.items?.find((p: any) => p?.then?.productId)
      ?.then?.productId ?? ""

  let product =
    (productSheetId
      ? truckProducts.find((p: any) => p.id === productSheetId)
      : null) ?? pickProductByProductId(truckProducts, productId)

  if (!product) {
    productId =
      schema?.properties?.parameters?.items?.find(
        (p: any) => p?.then?.productId && p?.then?.productId !== productId
      )?.then?.productId ?? ""

    product = pickProductByProductId(truckProducts, productId)
  }

  return { product, productId }
}

function getMarketValue(market: any): string | null {
  if (!market) return null
  return market?.domestic?.parameters ? "Domestic" : "Export"
}

function getMarketParameters(
  market: any,
  marketValue: string | null,
  fallbackParams: any[]
): any[] {
  if (!marketValue) return fallbackParams
  return marketValue === "Domestic"
    ? market?.domestic?.parameters
    : market?.export?.parameters
}

const SUBMITTED_HYDRATION_STATUSES = [
  "SUBMITTED",
  "SUBMITTED_PENDING_APPROVAL",
  "COMPLETED",
  "COMPLETED_PENDING_APPROVAL",
] as const

const KNOWN_PRODUCT_STATUSES = new Set<string>([
  ...Object.values(FormParamStatus),
])

function isSubmittedLikeStatus(status?: string): boolean {
  if (!status) return false
  return SUBMITTED_HYDRATION_STATUSES.includes(
    status as (typeof SUBMITTED_HYDRATION_STATUSES)[number]
  )
}

function resolveProductStatusForForm(
  sheetStatus: string,
  productStatus?: string,
  isResample: boolean = false
): FormParamStatus {
  if (productStatus === FormParamStatus.REJECTED_APPROVED) {
    return FormParamStatus.REJECTED_APPROVED
  }

  if (productStatus && !KNOWN_PRODUCT_STATUSES.has(productStatus)) {
    console.warn(
      "[resolveProductStatusForForm] Unknown product status received:",
      productStatus
    )
  }

  if (productStatus === FormParamStatus.REJECTED_PENDING_APPROVAL) {
    return FormParamStatus.REJECTED_PENDING_APPROVAL
  }

  const isSheetSubmitted = isSubmittedLikeStatus(sheetStatus)
  const isProductSubmitted = isSubmittedLikeStatus(productStatus)
  const shouldKeepDraft = isResample && !isSheetSubmitted && !isProductSubmitted

  if (shouldKeepDraft) {
    return FormParamStatus.DRAFT
  }

  // Completed states are intentionally treated as submitted-like for read-only parity in RM/PM UI.
  if (isSheetSubmitted || isProductSubmitted) {
    return FormParamStatus.SUBMITTED
  }

  return FormParamStatus.DRAFT
}

function resolvePmConditionValue(
  condition: any,
  marketValue: string | null,
  skuValue: string
): string | null | undefined {
  const conditionPath = condition?.path

  if (typeof conditionPath === "string") {
    const match = conditionPath.match(/parameters\.items\.?\[(\d+)\]\.value$/)
    if (match) {
      const paramIndex = Number(match[1])

      if (paramIndex === 0) {
        return marketValue
      }

      if (paramIndex === 1) {
        return skuValue || undefined
      }
    }
  }

  const expectedValue = condition?.const
  if (expectedValue === undefined) {
    return undefined
  }

  if (marketValue === expectedValue) {
    return marketValue
  }

  if (skuValue === expectedValue) {
    return skuValue
  }

  return undefined
}

function matchesPmParameterConditions(
  paramSchema: any,
  marketValue: string | null,
  skuValue: string
): boolean {
  const conditions = paramSchema?.if?.allOf
  if (!Array.isArray(conditions) || conditions.length === 0) {
    return true
  }

  return conditions.every((condition: any) => {
    const expectedValue = condition?.const
    if (expectedValue === undefined) {
      return true
    }

    const actualValue = resolvePmConditionValue(condition, marketValue, skuValue)
    if (actualValue === undefined || actualValue === null) {
      return false
    }

    return actualValue === expectedValue
  })
}

function resolvePmSkuSelectionValue(prodParams: any[], skuName: string): string {
  if (!skuName) {
    return ""
  }

  const skuParam = prodParams.find((param: any) => param?.options?.label === "SKU")
  const skuChoice = skuParam?.options?.choices?.find(
    (choice: any) =>
      choice?.value === skuName ||
      choice?.id === skuName ||
      choice?.label === skuName
  )

  return skuChoice?.value ?? skuChoice?.id ?? skuName
}

function buildPMProductParameters(
  prodParams: any[],
  parameters: any[],
  marketValue: string | null,
  skuValue: string,
  product: any,
  productId: string,
  effectiveProductStatus: FormParamStatus,
  targetForm: any
): void {
  // Parent product/SKU status is resolved by caller; this function only maps parameter rows.
  for (const prodParam of prodParams) {
    const label = prodParam?.options?.label ?? ""
    const status = effectiveProductStatus
    const defaultRecordedAt = parameters?.at(0)?.recordedAt ?? ""

    if (label === "Market") {
      targetForm.parameters.push({
        id: prodParam.id,
        label,
        parameterId: prodParam.parameterId,
        required: true,
        status,
        value: marketValue,
        recordedAt: defaultRecordedAt,
      })
      continue
    }

    if (label === "SKU") {
      const skuChoice = prodParam?.options?.choices?.find(
        (c: any) =>
          c?.value === skuValue || c?.id === skuValue || c?.label === skuValue
      )
      targetForm.parameters.push({
        id: prodParam.id,
        label,
        parameterId: prodParam.parameterId,
        required: true,
        status,
        value: skuChoice?.value ?? skuValue ?? "",
        recordedAt: defaultRecordedAt,
      })
      continue
    }

    const resolvedParamSchema = prodParam?.then
    if (!resolvedParamSchema) continue
    if (!matchesPmParameterConditions(prodParam, marketValue, skuValue)) {
      continue
    }

    const param = parameters.find(
      (p: any) => p.parameterId === resolvedParamSchema?.parameterId
    )

    targetForm.resample = product?.resample ?? false

    targetForm.parameters.push({
      id: resolvedParamSchema?.id ?? "",
      label: param?.name ?? resolvedParamSchema?.options?.label ?? "",
      parameterId: param?.parameterId ?? resolvedParamSchema?.parameterId,
      productId,
      required: resolvedParamSchema?.properties?.value?.required === true,
      status,
      value: param?.value ?? "",
      remark: param?.remark ?? "",
      recordedAt: param?.recordedAt ?? "",
    })

    if (param?.recordedAt) {
      targetForm.recordedAt = param.recordedAt
    }
  }
}

function buildPMProducts(
  metaSchema: any,
  truckProducts: any[],
  target: any,
  sheetStatus: string = ""
): void {
  const productItems = metaSchema?.properties?.products?.items ?? []

  for (const [index, productSchema] of productItems.entries()) {
    const productForm: any = {
      id: productSchema.id,
      label: productSchema?.options?.label ?? "",
      recordedAt: "",
      required: false,
      status: FormParamStatus.DRAFT,
      value: "",
      parameters: [],
      resample: false,
      skus: {
        label: "SKU",
        recordedAt: "",
        required: false,
        status: FormParamStatus.DRAFT,
        value: "",
      },
    }

    const { product, productId } = resolveProductFromSchema(
      productSchema,
      truckProducts
    )
    // Resolve product status once and reuse for all mapped params in this block.
    const effectiveProductStatus = resolveProductStatusForForm(
      sheetStatus,
      product?.status,
      product?.resample ?? false
    )

    productForm.status = effectiveProductStatus
    productForm.resample = product?.resample ?? false

    const sku = product?.sku?.length ? product.sku[0] : null
    const market = sku?.market ?? product?.market ?? null
    const marketValue = getMarketValue(market)
    const parameters = getMarketParameters(
      market,
      marketValue,
      product?.parameters ?? []
    )
    const prodParams = productSchema?.properties?.parameters?.items ?? []
    const skuValue = resolvePmSkuSelectionValue(prodParams, sku?.name ?? "")

    buildPMProductParameters(
      prodParams,
      parameters,
      marketValue,
      skuValue,
      product,
      productId,
      effectiveProductStatus,
      productForm
    )

    const skuItems = productSchema?.properties?.skus?.items ?? []
    for (const [skuIndex, skuSchema] of skuItems.entries()) {
      const skuForm: any = {
        id: skuSchema.id,
        label: skuSchema?.options?.label ?? "",
        recordedAt: "",
        required: false,
        status: FormParamStatus.DRAFT,
        value: "",
        parameters: [],
        resample: false,
      }

      const { product: skuProduct, productId: skuProductId } =
        resolveProductFromSchema(skuSchema, truckProducts)
      const skuItem = skuProduct?.sku?.length ? skuProduct.sku[0] : null
      const skuMarket = skuItem?.market ?? skuProduct?.market ?? null
      const skuMarketValue = getMarketValue(skuMarket)
      const skuParameters = getMarketParameters(
        skuMarket,
        skuMarketValue,
        skuProduct?.parameters ?? []
      )
      const effectiveSkuStatus = resolveProductStatusForForm(
        sheetStatus,
        skuProduct?.status,
        skuProduct?.resample ?? false
      )

      skuForm.status = effectiveSkuStatus
      skuForm.resample = skuProduct?.resample ?? false

      const skuParamsSchema = skuSchema?.properties?.parameters?.items ?? prodParams
      const skuSkuValue = resolvePmSkuSelectionValue(
        skuParamsSchema,
        skuItem?.name ?? ""
      )

      buildPMProductParameters(
        skuParamsSchema,
        skuParameters,
        skuMarketValue,
        skuSkuValue,
        skuProduct,
        skuProductId,
        effectiveSkuStatus,
        skuForm
      )

      if (skuForm.recordedAt) {
        productForm.skus.recordedAt = skuForm.recordedAt
      }

      productForm.skus[skuIndex] = skuForm
    }

    target.products[index] = productForm
  }
}

function buildRMProducts(
  metaSchema: any,
  truckProducts: any[],
  target: any,
  sheetStatus: string = ""
): void {
  const productItems = metaSchema?.properties?.products?.items ?? []

  for (const [index, productSchema] of productItems.entries()) {
    const productForm: any = {
      id: productSchema.id,
      label: productSchema?.options?.label ?? "",
      recordedAt: "",
      required: false,
      status: FormParamStatus.DRAFT,
      value: "",
      parameters: [],
      resample: false,
    }

    const paramItems = productSchema?.properties?.parameters?.items ?? []
    const productBySheetId = truckProducts.find(
      (p: any) => p.id === productSchema.productSheetId
    )
    const uniqueProductIds = Array.from(
      new Set(
        paramItems
          .map((p: any) => p?.productId)
          .filter((id: string | undefined): id is string => Boolean(id))
      )
    )
    const productByProductId =
      uniqueProductIds.length === 1
        ? truckProducts.find((p: any) => p.productId === uniqueProductIds[0])
        : undefined

    // Invariant: one schema product block should correspond to one logical product.
    const product = productBySheetId ?? productByProductId

    if (!product && uniqueProductIds.length > 1) {
      console.warn(
        "[buildRMProducts] Product schema maps to multiple productIds; using per-parameter fallback.",
        {
          sheetStatus,
          uniqueProductIds,
          productSheetId: productSchema.productSheetId,
        }
      )
    }

    const effectiveProductStatus = resolveProductStatusForForm(
      sheetStatus,
      product?.status,
      product?.resample ?? false
    )

    productForm.resample = product?.resample ?? false
    productForm.status = effectiveProductStatus

    for (const prodParam of paramItems) {
      const paramSourceProduct =
        product ??
        truckProducts.find((p: any) => p.productId === prodParam.productId)
      const param = paramSourceProduct?.parameters?.find(
        (p: any) => p.parameterId === prodParam.parameterId
      )

      productForm.parameters.push({
        id: prodParam?.properties?.id?.const ?? "",
        label: param?.name ?? prodParam?.options?.label ?? "",
        parameterId: prodParam?.parameterId ?? "",
        productId: prodParam?.productId ?? "",
        required: prodParam.properties?.value?.required === true,
        status: effectiveProductStatus,
        value: param?.value ?? "",
        remark: param?.remark ?? "",
        recordedAt: param?.recordedAt ?? "",
      })

      if (param?.recordedAt) {
        productForm.recordedAt = param.recordedAt
      }
    }

    target.products[index] = productForm
  }
}

function buildGeneralParams(
  metaSchema: any,
  truckParams: any[],
  target: any,
  isSheetSubmitted: boolean = true
): void {
  const paramStatus = isSheetSubmitted
    ? FormParamStatus.SUBMITTED
    : FormParamStatus.DRAFT
  const generalItems = metaSchema?.properties?.general?.items ?? []

  for (const [index, param] of generalItems.entries()) {
    const existingParam = truckParams.find(
      (p: any) => p.parameterId === param.parameterId
    )
    const isRequired = param.properties?.value?.required === true

    if (existingParam) {
      target.general[index] = {
        id: param.id,
        label: existingParam.name,
        parameterId: param.parameterId,
        recordedAt: existingParam.recordedAt,
        required: isRequired,
        status: paramStatus,
        value: existingParam.value ?? "",
        remark: existingParam.remark ?? "",
      }
      target.general.recordedAt = existingParam.recordedAt
      continue
    }

    const isNonEditable = param.options?.disabled === true
    const hasDefaultValue = param.properties?.value?.default !== undefined

    if (isNonEditable && hasDefaultValue) {
      const recordedAt = new Date().toISOString()
      target.general[index] = {
        id: param.id,
        label: param.options?.label ?? "",
        parameterId: param.parameterId,
        recordedAt,
        required: isRequired,
        status: paramStatus,
        value: param.properties?.value?.default ?? "",
        remark: "",
      }

      if (!target.general.recordedAt) {
        target.general.recordedAt = recordedAt
      }
    }
  }
}

function buildBaseTruckForm(sheetId: string, isSheetSubmitted: boolean = true) {
  const status = isSheetSubmitted
    ? FormParamStatus.SUBMITTED
    : FormParamStatus.DRAFT
  return {
    sheetId,
    general: {
      label: "General Parameters",
      recordedAt: "",
      required: false,
      status,
      value: "",
    },
    products: {
      label: "Products",
      recordedAt: "",
      required: false,
      status,
      value: "",
    },
  }
}

export async function processResampleStructure(
  resamples: Handover[]
): Promise<void> {
  const rmpmStore = useStore.getState().rmpm

  for (const handover of resamples) {
    for (const truck of handover.trucks.rm ?? []) {
      rmpmStore.setRmForm(buildTruckForm(rmpmStore, truck, "rm"))
    }
    for (const truck of handover.trucks.pm ?? []) {
      rmpmStore.setPmForm(buildTruckForm(rmpmStore, truck, "pm"))
    }
  }
}

function buildTruckForm(
  rmpmStore: ReturnType<typeof useStore.getState>["rmpm"],
  truck: any,
  type: "rm" | "pm"
): ReturnType<typeof buildBaseTruckForm> {
  const sheetStatus = FormParamStatus.DRAFT
  const isSheetSubmitted = isSubmittedLikeStatus(sheetStatus)
  const form = buildBaseTruckForm(truck.sheetId, isSheetSubmitted)
  const metaSchema = rmpmStore.getTruckMetaSchemaById(type, truck.sheetId)
  const truckParams = truck.truckParameters ?? []
  const truckProducts = truck.products ?? []

  buildGeneralParams(metaSchema, truckParams, form, isSheetSubmitted)

  if (type === "rm") {
    buildRMProducts(metaSchema, truckProducts, form, sheetStatus)
  } else {
    buildPMProducts(metaSchema, truckProducts, form, sheetStatus)
  }

  return form
}

export async function fetchResample() {
  const resample = await getRMPMResample()

  const handoverItems: any =
    (resample as any).handovers || (resample as any).handover || []

  processResample(handoverItems)
}

export async function processResample(handoverItems: any[]) {
  const rmpmStore = useStore.getState().rmpm

  // Load each RM/PM truck into store
  for (let handoverItem of handoverItems) {
    for (let item of handoverItem.products) {
      const truckType = item.type.toLowerCase() === "rm" ? "rm" : "pm"
      const truckSchema = buildSchemaFromSessionSheet(rmpmStore, item, truckType)

      if (!truckSchema?.sheetId) {
        console.warn(
          "[processResample] Could not build schema for resample sheet:",
          item?.id
        )
        continue
      }

      if (truckType === "rm") {
        rmpmStore.addRmTrucksMetaSchemas([truckSchema])
      } else {
        rmpmStore.addPmTrucksMetaSchemas([truckSchema])
      }

      rmpmStore.removeToSubmitted(truckSchema.sheetId)
    }
  }

  const resampleStructure: Handover[] = handoverItems.map((handover: any) => {
    const trucks: HandoverTrucks = { rm: [], pm: [] }

    handover.products.forEach(
      (product: {
        products: Schema[]
        truckParameters: TruckParameter[]
        truckNumber: string
        id?: string
        sheetId: string
        type: string
      }) => {
        const type = product.type.toLowerCase() === "rm" ? "rm" : "pm"

        trucks[type].push({
          truckNumber: product.truckNumber,
          sheetId: product.id ?? product.sheetId,
          truckParameters: product.truckParameters,
          products: product.products,
        })
      }
    )

    return {
      handoverFromEmployeeCode: handover.handoverFromEmployeeCode,
      handoverFromEmployeeSessionId: handover.handoverFromEmployeeSessionId,
      trucks,
    }
  })

  processResampleStructure(resampleStructure)

  rmpmStore.setResample(resampleStructure ?? [])
}

// ---------- Hydrate from Session Sheets (for cross-device sync) ----------

function getTruckType(sheetType: string): "rm" | "pm" {
  return sheetType.toLowerCase() === "rm" ? "rm" : "pm"
}

const PM_TYPE_MAP: Record<string, string> = {
  PM_CARTON: "CARTON",
  PM_LAMINATE: "LAMINATE",
}

function extractPmTypeFromSheetType(sheetType: string): string | null {
  return PM_TYPE_MAP[sheetType] ?? null
}

/**
 * Process session sheets data and hydrate the local store.
 * This enables cross-device sync by loading existing sheets when logging in from a different device.
 * Handles all three sheet types: PROCESS, HANDOVER, and RESAMPLE.
 */
type SheetWithSchema = { schema: Schema; sheet: SessionSheet }
type SheetGroup = { rm: SheetWithSchema[]; pm: SheetWithSchema[] }

function mapTruckParameters(
  params: SessionSheetTruckParameter[]
): TruckParameter[] {
  return params.map((p) => ({
    id: p.parameterId,
    name: p.name,
    value: p.value,
    remark: p.remark ?? undefined,
    recordedAt: p.recordedAt,
    status: p.status as FormParamStatus,
    inputType: p.inputType as ParamSchemaType,
  }))
}

function buildTruckFromSheetData({ schema, sheet }: SheetWithSchema): {
  truckNumber: string
  sheetId: string
  products: Schema[]
  truckParameters: TruckParameter[]
} {
  return {
    truckNumber: sheet.truckNumber,
    sheetId: schema.sheetId ?? sheet.id,
    products: schema.properties?.products?.items ?? [],
    truckParameters: mapTruckParameters(sheet.truckParameters ?? []),
  }
}

function addToSheetGroup(
  map: Map<string, SheetGroup>,
  handoverId: string,
  truckType: "rm" | "pm",
  data: SheetWithSchema
): void {
  let group = map.get(handoverId)
  if (!group) {
    group = { rm: [], pm: [] }
    map.set(handoverId, group)
  }
  group[truckType].push(data)
}

function buildHandoverEntries(sessionMap: Map<string, SheetGroup>): Handover[] {
  return Array.from(sessionMap.entries()).map(([sessionId, trucks]) => ({
    handoverFromEmployeeCode: "",
    handoverFromEmployeeSessionId: sessionId,
    trucks: {
      rm: trucks.rm.map(buildTruckFromSheetData),
      pm: trucks.pm.map(buildTruckFromSheetData),
    },
  }))
}

function filterProductsForSheetHydration(
  sheetStatus: string,
  products: SessionSheetProduct[]
): SessionSheetProduct[] {
  if (!isSubmittedLikeStatus(sheetStatus)) {
    return products
  }

  const filtered = products.filter(
    (product) =>
      isSubmittedLikeStatus(product?.status) ||
      product?.status === FormParamStatus.REJECTED_PENDING_APPROVAL ||
      product?.status === FormParamStatus.REJECTED_APPROVED
  )

  // Keep original payload when backend status values are unexpected.
  return filtered.length ? filtered : products
}

/**
 * Build a schema from session sheet data when no stored schema is available.
 * This handles newly created sheets that haven't been submitted yet.
 */
function buildSchemaFromSessionSheet(
  rmpmStore: ReturnType<typeof useStore.getState>["rmpm"],
  sheet: SessionSheet,
  truckType: "rm" | "pm"
): Schema | null {
  const sheetId = sheet.id
  const effectiveSheetType = sheet.sheetType ?? "PROCESS"
  const isHandover = effectiveSheetType === "HANDOVER"
  const isResample = effectiveSheetType === "RESAMPLE"
  const products = filterProductsForSheetHydration(
    sheet.status,
    sheet.products ?? []
  )

  if (truckType === "rm") {
    // Build RM truck schema
    const newSchema: Schema = {
      isHandover,
      isResample,
      sheetId: sheetId,
      type: "object",
      options: {
        label: sheet.truckNumber,
      },
      properties: {
        id: {
          type: "string",
          default: sheetId,
          const: sheetId,
        },
        truckNumber: {
          type: "string",
          const: sheet.truckNumber,
        },
        general: {
          type: "array",
          options: {
            widget: ParamSchemaType.CARD,
            label: "General Parameters",
            hideLabel: true,
          },
          items: [],
        },
        products: {
          type: "array",
          options: {
            label: "Products",
            addable: true,
            widget: ParamSchemaType.SECTION,
          },
          items: [],
        },
      },
    }

    // Add general parameters
    const generalSchema = newSchema.properties?.general as Schema
    if (!generalSchema.items) {
      generalSchema.items = []
    }

    const rmProductApiSchema = rmpmStore.getRmParametersApiSchema()
    if (rmProductApiSchema?.length) {
      const generalItems = buildSchemaForGeneralParameter(rmProductApiSchema)
      generalSchema.items.push(...generalItems)
    }

    // Add product schemas from session sheet products
    const productSchema = newSchema.properties?.products as Schema
    if (!productSchema.items) {
      productSchema.items = []
    }

    for (const product of products) {
      if (!product.productId) continue

      const rmApiSchema = rmpmStore.getRmProductApiSchema(product.productId)
      if (!rmApiSchema) continue

      const productBlock = buildSchemaForRmProduct(rmApiSchema)
      // Apply productSheetId from the session sheet product
      productBlock.productSheetId = product.id
      productSchema.items.push(productBlock)
    }

    return newSchema
  } else {
    // Build PM truck schema
    const pmTypeValue = extractPmTypeFromSheetType(sheet.type)

    const newSchema: Schema = {
      isHandover,
      isResample,
      sheetId: sheetId,
      type: "object",
      options: {
        label: sheet.truckNumber,
      },
      properties: {
        id: {
          type: "string",
          default: sheetId,
          const: sheetId,
        },
        truckNumber: {
          type: "string",
          const: sheet.truckNumber,
        },
        general: {
          type: "array",
          options: {
            widget: ParamSchemaType.CARD,
          },
          items: [],
        },
        products: {
          type: "array",
          options: {
            widget: ParamSchemaType.SECTION,
            addable: true,
            label: "Products",
          },
          items: [],
        },
      },
    }

    // Add general parameters
    const generalSchema = newSchema.properties?.general as Schema
    if (!generalSchema.items) {
      generalSchema.items = []
    }

    const pmProductApiSchema = rmpmStore.getPmParametersApiSchema()
    if (pmProductApiSchema?.length) {
      const generalParameters = buildSchemaForGeneralParameter(
        pmProductApiSchema ?? []
      )

      if (
        generalParameters.some((param) => param.options?.label === "PM Type")
      ) {
        generalSchema.items.push(...generalParameters)
      } else if (pmTypeValue) {
        // Add PM Type as a non-editable field
        const paramID = createUuid()

        const baseItem: Schema = {
          id: paramID,
          type: "object",
          required: true,
          options: {
            label: "PM Type",
            widget: ParamSchemaType.VALUE_TEXT,
            mode: ParamSchemaMode.INFO,
            disabled: true,
          },
          properties: {
            id: {
              type: "string",
              default: paramID,
              required: true,
            },
            recordedAt: {
              type: "string",
              required: true,
            },
            value: {
              type: "string",
              required: true,
              default: pmTypeValue,
            },
          },
        }

        generalSchema.items.push(baseItem, ...generalParameters)
      } else {
        generalSchema.items.push(...generalParameters)
      }
    }

    // Add product schemas from session sheet products
    const productSchema = newSchema.properties?.products as Schema
    if (!productSchema.items) {
      productSchema.items = []
    }

    for (const product of products) {
      if (!product.productId) continue

      const pmApiSchema = rmpmStore.getPmProductApiSchema(product.productId)
      if (!pmApiSchema) continue

      const schemaPath = `properties.products.items[${productSchema.items.length}]`
      const productSchemaItem = buildSchemaForPmProduct(pmApiSchema, schemaPath)
      // Apply productSheetId from the session sheet product
      productSchemaItem.productSheetId = product.id
      productSchema.items.push(productSchemaItem)
    }

    return newSchema
  }
}

function hydrateProcessSheet(
  rmpmStore: ReturnType<typeof useStore.getState>["rmpm"],
  truckSchema: Schema,
  sheet: SessionSheet,
  truckType: "rm" | "pm"
): void {
  delete truckSchema.isHandover

  const sheetId = truckSchema.sheetId!
  const isSheetSubmitted = isSubmittedLikeStatus(sheet.status)

  if (truckType === "rm") {
    rmpmStore.addRmTrucksMetaSchemas([truckSchema])
  } else {
    rmpmStore.addPmTrucksMetaSchemas([truckSchema])
  }

  const form = buildBaseTruckForm(sheetId, isSheetSubmitted)
  const metaSchema = rmpmStore.getTruckMetaSchemaById(truckType, sheetId)
  const truckParams = sheet.truckParameters ?? []
  const truckProducts = filterProductsForSheetHydration(
    sheet.status,
    sheet.products ?? []
  )

  buildGeneralParams(metaSchema, truckParams, form, isSheetSubmitted)

  if (truckType === "rm") {
    buildRMProducts(metaSchema, truckProducts, form, sheet.status)
    rmpmStore.setRmForm(form)
  } else {
    buildPMProducts(metaSchema, truckProducts, form, sheet.status)
    rmpmStore.setPmForm(form)
  }

  if (isSheetSubmitted) {
    rmpmStore.addToSubmitted(sheetId)
  }
}

export function hydrateFromSessionSheets(
  response: GetSessionSheetsResponse
): void {
  const rmpmStore = useStore.getState().rmpm

  // Clear existing local data before hydrating from backend
  rmpmStore.clearFormData()

  if (!response.sheets?.length) {
    return
  }

  const handoversBySession = new Map<string, SheetGroup>()
  const resamplesBySession = new Map<string, SheetGroup>()

  for (const sheet of response.sheets) {
    const truckType = getTruckType(sheet.type)

    try {
      // Always build schema from sheet data - don't depend on stored schema
      const truckSchema = buildSchemaFromSessionSheet(
        rmpmStore,
        sheet,
        truckType
      )

      if (!truckSchema) {
        console.warn(
          "[hydrateFromSessionSheets] Could not build schema for sheet:",
          sheet.id
        )
        continue
      }

      const effectiveSheetType = sheet.sheetType ?? "PROCESS"

      if (effectiveSheetType === "PROCESS") {
        hydrateProcessSheet(rmpmStore, truckSchema, sheet, truckType)
      } else if (effectiveSheetType === "HANDOVER" && sheet.handoverId) {
        addToSheetGroup(handoversBySession, sheet.handoverId, truckType, {
          schema: truckSchema,
          sheet,
        })
      } else if (effectiveSheetType === "RESAMPLE") {
        hydrateProcessSheet(rmpmStore, truckSchema, sheet, truckType)
      }
    } catch (error) {
      console.error(
        "[hydrateFromSessionSheets] Failed to process sheet:",
        sheet.id,
        error
      )
    }
  }

  if (handoversBySession.size > 0) {
    rmpmStore.setHandover(buildHandoverEntries(handoversBySession))
  }

  if (resamplesBySession.size > 0) {
    rmpmStore.setResample(buildHandoverEntries(resamplesBySession))
  }
}
